import { useState } from 'react';
import { Link } from 'react-router-dom';
import { startOfWeek, endOfWeek, addWeeks, subWeeks, format } from 'date-fns';
import {
  useMyAppointments,
  useConfirmAppointment,
  useCancelAppointment,
  useRescheduleAppointment,
} from '@/hooks/scheduling';
import { useAuth } from '@/hooks';
import {
  useDoctorAvailability,
  useCreateDoctorAvailabilityException,
  useDeleteDoctorAvailabilityException,
} from '@/hooks/doctor/useDoctorAvailability';
import {
  AppointmentList,
  CancelDialog,
  RescheduleDialog,
  WeeklyCalendarHeader,
  WeeklyGrid,
  MobileDayCalendar,
  AppointmentDetailDrawer,
} from '@/components/scheduling';
import type { AppointmentListItem } from '@/api';
import { cn } from '@/utils/cn';
import { useToast } from '@/components/ui/use-toast';
import { Clock } from 'lucide-react';
import { Button } from '@/components/ui/Button';

export function DoctorSchedulePage() {
  const { user } = useAuth();
  const doctorId = user?.id || '';

  // Modo de visualização: 'grid' (Grade Semanal - padrão) ou 'list' (Lista clássica)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [activeTab, setActiveTab] = useState<'today' | 'upcoming' | 'past'>('today');

  // Estado de datas para a navegação semanal e mobile
  const [currentWeekDate, setCurrentWeekDate] = useState<Date>(new Date());
  const [mobileSelectedDate, setMobileSelectedDate] = useState<Date>(new Date());

  // Diálogos e Drawer de detalhes
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentListItem | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState<string | null>(null);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [appointmentToReschedule, setAppointmentToReschedule] = useState<{ id: string; doctorId: string } | null>(null);
  const { toast } = useToast();

  // Intervalos para as abas da visualização em lista
  const todayStr = new Date().toISOString().split('T')[0] ?? '';
  const todayEnd = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0] ?? '';
  const futureEnd = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] ?? '';
  const pastStart = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] ?? '';

  const { data: todayData, isLoading: isLoadingToday, isError: isErrorToday, refetch: refetchToday } = useMyAppointments({
    date: todayStr,
  });

  const { data: upcomingData, isLoading: isLoadingUpcoming, isError: isErrorUpcoming, refetch: refetchUpcoming } = useMyAppointments({
    from: todayEnd,
    to: futureEnd,
  });

  const { data: pastData, isLoading: isLoadingPast, isError: isErrorPast, refetch: refetchPast } = useMyAppointments({
    from: pastStart,
    to: todayStr,
  });

  // Intervalo da semana ativa para a grade gráfica semanal
  const activeWeekStart = startOfWeek(currentWeekDate, { weekStartsOn: 1 });
  const activeWeekEnd = endOfWeek(currentWeekDate, { weekStartsOn: 1 });
  const weekFromStr = format(activeWeekStart, 'yyyy-MM-dd');
  const weekToStr = format(new Date(activeWeekEnd.getTime() + 24 * 60 * 60 * 1000), 'yyyy-MM-dd');

  const {
    data: weekData,
    isLoading: isLoadingWeek,
    refetch: refetchWeek,
  } = useMyAppointments({
    from: weekFromStr,
    to: weekToStr,
  });

  // Disponibilidade e bloqueios (exceções) do médico
  const { data: availabilityData, refetch: refetchAvailability } = useDoctorAvailability(doctorId);
  const createException = useCreateDoctorAvailabilityException(doctorId);
  const deleteException = useDeleteDoctorAvailabilityException(doctorId);
  const exceptions = availabilityData?.exceptions || [];

  // Mutações de agendamento
  const confirmAppointment = useConfirmAppointment();
  const cancelAppointment = useCancelAppointment();
  const rescheduleAppointment = useRescheduleAppointment();

  // Navegação da semana
  const handlePrevWeek = () => {
    setCurrentWeekDate((prev) => subWeeks(prev, 1));
  };

  const handleNextWeek = () => {
    setCurrentWeekDate((prev) => addWeeks(prev, 1));
  };

  const handleToday = () => {
    const now = new Date();
    setCurrentWeekDate(now);
    setMobileSelectedDate(now);
  };

  // Abertura do Drawer de detalhes ao clicar na consulta da grade
  const handleSelectAppointment = (appointment: AppointmentListItem) => {
    setSelectedAppointment(appointment);
    setDrawerOpen(true);
  };

  // Bloqueio rápido ao clicar em horário vago
  const handleBlockSlot = async (date: Date, startTime: string, endTime: string) => {
    if (!doctorId) return;
    try {
      await createException.mutateAsync({
        date,
        isBlocked: true,
        startTime,
        endTime,
        reason: 'Bloqueado na grade',
      });
      refetchAvailability();
      toast({
        title: 'Horário bloqueado',
        description: `Horário ${startTime} - ${endTime} em ${format(date, 'dd/MM')} bloqueado com sucesso.`,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao bloquear horário';
      toast({
        title: 'Erro ao bloquear',
        description: message,
        variant: 'error',
      });
    }
  };

  // Desbloqueio ao clicar em horário bloqueado
  const handleUnblockSlot = async (exceptionId: string) => {
    if (!doctorId) return;
    try {
      await deleteException.mutateAsync(exceptionId);
      refetchAvailability();
      toast({
        title: 'Horário desbloqueado',
        description: 'O bloqueio foi removido com sucesso.',
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao desbloquear horário';
      toast({
        title: 'Erro ao desbloquear',
        description: message,
        variant: 'error',
      });
    }
  };

  const handleCancel = (appointmentId: string) => {
    setAppointmentToCancel(appointmentId);
    setCancelDialogOpen(true);
  };

  const handleReschedule = (appointmentId: string) => {
    const allAppointments = [
      ...(weekData?.appointments || []),
      ...(todayData?.appointments || []),
      ...(upcomingData?.appointments || []),
      ...(pastData?.appointments || []),
    ];
    const found = allAppointments.find((a) => a.id === appointmentId);
    if (found) {
      const docId = (found as any).doctorId || found.doctor?.id || doctorId || '';
      setAppointmentToReschedule({ id: found.id, doctorId: docId });
      setRescheduleDialogOpen(true);
    }
  };

  const handleConfirmReschedule = async (newSlotId: string) => {
    if (!appointmentToReschedule) return;
    try {
      await rescheduleAppointment.mutateAsync({
        appointmentId: appointmentToReschedule.id,
        newSlotId,
      });
      setRescheduleDialogOpen(false);
      setAppointmentToReschedule(null);
      setDrawerOpen(false);
      refetchWeek();
      refetchToday();
      refetchUpcoming();
      refetchPast();
      toast({
        title: 'Consulta reagendada',
        description: 'A consulta foi reagendada com sucesso.',
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao reagendar consulta';
      toast({
        title: 'Erro ao reagendar',
        description: message,
        variant: 'error',
      });
    }
  };

  const handleConfirmCancel = async (reason: string) => {
    if (!appointmentToCancel) return;
    try {
      await cancelAppointment.mutateAsync({
        appointmentId: appointmentToCancel,
        reason,
      });
      setCancelDialogOpen(false);
      setAppointmentToCancel(null);
      setDrawerOpen(false);
      refetchWeek();
      refetchToday();
      refetchUpcoming();
      refetchPast();
      toast({
        title: 'Consulta cancelada',
        description: 'A consulta foi cancelada com sucesso.',
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao cancelar consulta';
      toast({
        title: 'Erro ao cancelar',
        description: message,
        variant: 'error',
      });
    }
  };

  const handleConfirm = async (appointmentId: string) => {
    try {
      await confirmAppointment.mutateAsync(appointmentId);
      refetchWeek();
      refetchToday();
      refetchUpcoming();
      refetchPast();
      toast({
        title: 'Consulta confirmada',
        description: 'A consulta foi confirmada com sucesso.',
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao confirmar consulta';
      toast({
        title: 'Erro ao confirmar',
        description: message,
        variant: 'error',
      });
    }
  };

  return (
    <div className="container max-w-7xl py-6 space-y-6">
      {/* Cabeçalho da Página */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Minha Agenda</h1>
          <p className="text-muted-foreground mt-1">
            Gerencie suas consultas semanais, horários livres e bloqueios de agenda
          </p>
        </div>
        <Link to="/medico/disponibilidade">
          <Button variant="outline" size="sm" className="gap-2 rounded-xl border-border hover:border-primary/50">
            <Clock className="w-4 h-4 text-primary" />
            <span>Configurar Horários e Folgas</span>
          </Button>
        </Link>
      </div>

      {/* Navegação Semanal e Alternador Grade / Lista */}
      <WeeklyCalendarHeader
        currentDate={currentWeekDate}
        onPrevWeek={handlePrevWeek}
        onNextWeek={handleNextWeek}
        onToday={handleToday}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        isLoading={isLoadingWeek}
      />

      {/* Visualização em Grade Semanal (Padrão) */}
      {viewMode === 'grid' && (
        <>
          {/* Desktop: Grade semanal de 7 colunas */}
          <div className="hidden md:block">
            <WeeklyGrid
              startDate={currentWeekDate}
              appointments={weekData?.appointments || []}
              exceptions={exceptions}
              isLoading={isLoadingWeek}
              onSelectAppointment={handleSelectAppointment}
              onBlockSlot={handleBlockSlot}
              onUnblockSlot={handleUnblockSlot}
            />
          </div>

          {/* Mobile: Visão diária otimizada com seletor de dias */}
          <div className="block md:hidden">
            <MobileDayCalendar
              weekStart={currentWeekDate}
              selectedDate={mobileSelectedDate}
              onSelectDate={setMobileSelectedDate}
              appointments={weekData?.appointments || []}
              exceptions={exceptions}
              isLoading={isLoadingWeek}
              onSelectAppointment={handleSelectAppointment}
              onBlockSlot={handleBlockSlot}
              onUnblockSlot={handleUnblockSlot}
            />
          </div>
        </>
      )}

      {/* Visualização em Lista Clássica (Hoje / Próximas / Anteriores) */}
      <div className={cn(viewMode !== 'list' && 'hidden')}>
        <div className="flex border-b border-border mb-6">
          <button
            type="button"
            onClick={() => setActiveTab('today')}
            className={cn(
              'px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors',
              activeTab === 'today'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            Hoje
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('upcoming')}
            className={cn(
              'px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors',
              activeTab === 'upcoming'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            Próximas
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('past')}
            className={cn(
              'px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors',
              activeTab === 'past'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            Anteriores
          </button>
        </div>

        {activeTab === 'today' && (
          <AppointmentList
            appointments={todayData?.appointments || []}
            isLoading={isLoadingToday}
            isError={isErrorToday}
            userRole="doctor"
            emptyTitle="Nenhuma consulta hoje"
            emptyDescription="Você não tem consultas agendadas para hoje."
            onCancel={handleCancel}
            onConfirm={handleConfirm}
            onReschedule={handleReschedule}
            onRetry={() => refetchToday()}
          />
        )}

        {activeTab === 'upcoming' && (
          <AppointmentList
            appointments={upcomingData?.appointments || []}
            isLoading={isLoadingUpcoming}
            isError={isErrorUpcoming}
            userRole="doctor"
            emptyTitle="Nenhuma consulta futura"
            emptyDescription="Você não tem consultas agendadas para os próximos dias."
            onCancel={handleCancel}
            onConfirm={handleConfirm}
            onReschedule={handleReschedule}
            onRetry={() => refetchUpcoming()}
          />
        )}

        {activeTab === 'past' && (
          <AppointmentList
            appointments={pastData?.appointments || []}
            isLoading={isLoadingPast}
            isError={isErrorPast}
            userRole="doctor"
            emptyTitle="Nenhuma consulta anterior"
            emptyDescription="Suas consultas passadas aparecerão aqui."
            onRetry={() => refetchPast()}
          />
        )}
      </div>

      {/* Gaveta Lateral de Detalhes da Consulta */}
      <AppointmentDetailDrawer
        appointment={selectedAppointment}
        isOpen={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedAppointment(null);
        }}
        onCancel={handleCancel}
        onReschedule={(apt) => handleReschedule(apt.id)}
        onConfirm={handleConfirm}
        isConfirming={confirmAppointment.isPending}
      />

      {/* Diálogo de Cancelamento */}
      <CancelDialog
        isOpen={cancelDialogOpen}
        onClose={() => {
          setCancelDialogOpen(false);
          setAppointmentToCancel(null);
        }}
        onConfirm={handleConfirmCancel}
        isLoading={cancelAppointment.isPending}
      />

      {/* Diálogo de Reagendamento */}
      {appointmentToReschedule && (
        <RescheduleDialog
          isOpen={rescheduleDialogOpen}
          onClose={() => {
            setRescheduleDialogOpen(false);
            setAppointmentToReschedule(null);
          }}
          onConfirm={handleConfirmReschedule}
          doctorId={appointmentToReschedule.doctorId}
          isLoading={rescheduleAppointment.isPending}
        />
      )}
    </div>
  );
}
